import { useEffect, useState } from 'react';

import type { MobiLauncherConfig } from '@schema/index.js';
import type { Profile } from '@schema/profile.js';

/**
 * Profile cycling for signage / unattended displays.
 *
 * When all of these are true at once:
 *   - `signageEntitled` is true (the `signageMode` feature is on)
 *   - `config.signage?.rotationIntervalSeconds` is a number
 *   - `config.profiles.length >= 2`
 *   - `anchorProfileId` exists in `config.profiles`
 *
 * …the hook starts a window.setInterval that walks `profiles[]` in
 * array order at the configured cadence and returns the currently
 * rotated profile. Otherwise it returns null and the caller falls
 * back to whatever profile was passed in (engine-resolved for the
 * production launcher, `config.profiles[0]` for the Builder preview).
 *
 * Re-anchors whenever `anchorProfileId` changes — useful when a
 * remote-config update swaps which profile the engine resolves to,
 * or when the Builder preview's first profile changes because the
 * customer reordered `profiles[]`. The cycle resumes from that new
 * anchor's index, so rotation doesn't lurch back to 0.
 *
 * Shared by App.tsx (the live launcher) and PreviewApp.tsx (the
 * Builder iframe) so the customer can verify their rotation
 * configuration before pushing it to a device.
 */
export function useSignageRotation(
  config: MobiLauncherConfig | null,
  anchorProfileId: string | null,
  signageEntitled: boolean,
): Profile | null {
  const [idx, setIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!signageEntitled || config === null || anchorProfileId === null) {
      setIdx(null);
      return undefined;
    }
    const interval = config.signage?.rotationIntervalSeconds;
    if (typeof interval !== 'number' || config.profiles.length < 2) {
      setIdx(null);
      return undefined;
    }
    const anchor = config.profiles.findIndex((p) => p.id === anchorProfileId);
    setIdx(anchor >= 0 ? anchor : 0);
    const handle = window.setInterval(() => {
      setIdx((prev) => {
        const cur = prev ?? 0;
        return (cur + 1) % config.profiles.length;
      });
    }, interval * 1000);
    return () => {
      window.clearInterval(handle);
      setIdx(null);
    };
  }, [signageEntitled, config, anchorProfileId]);

  if (idx === null || config === null) return null;
  return config.profiles[idx] ?? null;
}
